import clsx from "clsx";
import { useToasts } from "react-toast-notifications";

import { useUser } from "@components/user-context";
import {
  CompareDatePicker,
  DatePicker,
  SecondaryIconButton,
  Select,
} from "@components/v2";
import { useLogger, usePrettyLogEventPage } from "@components/v2/analytics";
import { useOverviewContext } from "@features/overview-v2/hooks";
import { QuickFilterSelector } from "@features/quick-filter-selector";
import {
  Granularity,
  type IGranularity,
  TimeGranularityV2,
} from "@north-beam/nb-common";

import { SaveMenu } from "./save-menu";

interface Props {
  isShadowVisible: boolean;
}

export const TopBar = ({ isShadowVisible }: Props) => {
  const prettyLogEventPage = usePrettyLogEventPage();
  const logger = useLogger({ container: "date-ranges" });
  const { addToast } = useToasts();

  const { user } = useUser();
  const {
    filters: {
      attributionModel,
      attributionWindow,
      accountingMode,
      timeGranularity,
      dateRange,
      dateRangeInterval,
      compareDateRange,
      level,
    },
    savedViews,
    selectedSavedView,
    setDateRange,
    setCompareDateRange,
    setTimeGranularity,
    setLevel,
    selectSavedView,
  } = useOverviewContext();

  const pageContext = {
    dateRange,
    attributionModel,
    attributionWindow,
    accountingMode,
    timeGranularity,
    dateRangeInterval,
    compareDateRange,
    level,
  };

  const timeGranularityOptions = TimeGranularityV2.reactSelectOptions.filter(
    (e) => e.value !== "hourly",
  );

  const levelOptions = Granularity.reactSelectOptions.filter(
    (e) => e.value !== "platform",
  ) as { value: Exclude<IGranularity, "platform">; label: string }[];

  return (
    <div
      className={clsx(
        "px-6 py-4 sticky top-0 z-50 bg-v2-glass-inverse backdrop-blur-sm",
        "transition delay-150 duration-300 flex gap-2",
        { "drop-shadow-[0_0px_10px_rgba(0,0,0,0.1)]": isShadowVisible },
      )}
    >
      <div className="flex gap-2">
        <DatePicker
          className="w-32"
          value={dateRange}
          onChange={(val) => {
            if (val) {
              setDateRange(val);

              prettyLogEventPage({
                domain: "Top Level Filters",
                action: "onChange",
                additionalActionInfo: "Set Date Range",
                data: {
                  newDateRange: val,
                  pageContext,
                },
              });

              if (typeof val === "string") {
                logger.event.overviewPageSelectPredefinedDateRange("click", {
                  eventDetails: {
                    shortcutDateRange: val,
                  },
                });
              } else {
                logger.event.overviewPageSelectCustomDateRange("click", {
                  eventDetails: {
                    ...val,
                  },
                });
              }
            }
          }}
        />
        <Select
          buttonClassName="w-24"
          size="md"
          options={timeGranularityOptions}
          value={timeGranularity}
          onChange={(val) => {
            if (val) {
              prettyLogEventPage({
                domain: "Filters Bar",
                action: "onChange",
                additionalActionInfo: "Set Time Granularity",
                data: {
                  newTimeGranularity: val,
                  pageContext,
                },
              });

              setDateRange({
                startDate: dateRangeInterval.startDate,
                endDate: dateRangeInterval.endDate,
                type: "fixed",
              });
              setTimeGranularity(val);
            }
          }}
        />
        <CompareDatePicker
          className="w-48"
          value={compareDateRange}
          referenceDateRange={dateRangeInterval}
          timezone={user.timezone}
          timeGranularity={timeGranularity}
          onChange={(val) => {
            if (val) {
              setCompareDateRange(val);

              prettyLogEventPage({
                domain: "Filters Bar",
                action: "onChange",
                additionalActionInfo: "Set Compare Date Range",
                data: {
                  newCompareDateRange: val,
                  pageContext,
                },
              });
            }
          }}
        />
        <Select
          buttonClassName="w-28"
          size="md"
          options={levelOptions}
          value={level}
          onChange={(val) => {
            if (val) {
              prettyLogEventPage({
                domain: "Top Bar",
                action: "onChange",
                additionalActionInfo: "Set Level",
                data: {
                  newLevel: val,
                  pageContext,
                },
              });

              setLevel(val);
            }
          }}
        />
      </div>
      <div className="flex-1 overflow-hidden">
        {selectedSavedView && (
          <QuickFilterSelector
            value={selectedSavedView}
            options={savedViews.map((e) => ({
              ...e,
              isDefault: e.is_template,
            }))}
            onChange={(savedView) => {
              selectSavedView(savedView, {
                eventType: "select",
                container: "saved-views-menu",
              });
            }}
          />
        )}
      </div>
      <div className="flex gap-2">
        <SaveMenu />
        <SecondaryIconButton
          className="group"
          onClick={async () => {
            await navigator.clipboard.writeText(window.location.href);
            addToast("Copied to clipboard!", { appearance: "success" });
          }}
        >
          <i
            className={clsx(
              "fa-regular fa-paper-plane text-sm text-v2-secondary group-hover:text-v2-action",
            )}
          />
        </SecondaryIconButton>
      </div>
    </div>
  );
};
